import { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Login      from './components/auth/Login';
import Layout     from './components/layout/Layout';
import ShopsOverview from './components/layout/ShopsOverview';
import Reports from './components/layout/Reports';
import Personal   from './components/sections/Personal';
import ExtraDetails      from './components/sections/Other';
import Family     from './components/sections/Family';
import Qualification  from './components/sections/Education';
import Contact    from './components/sections/Contact';
import Employee   from './components/sections/Employee';
import Vehicles   from './components/sections/Vehicles';
import Items      from './components/sections/Items';
import Documents  from './components/sections/Documents';
import RegistrationTimeline from './components/sections/RegistrationTimeline';
import Meetings   from './components/sections/Meetings';
import Owners     from './components/sections/Owners';
import Rent       from './components/sections/Rent';
import IssuesBonds from './components/sections/IssuesBonds';
import PaymentDetails from './components/sections/PaymentDetails';
import PositionType from './components/sections/PositionType';
import api from './utils/api';
import './index.css';

const TAB_TITLES = {
  personal:  'Personal Information',
  extra_details:     'Extra Details',
  family:    'Family Members',
  qualification: 'Qualification',
  contact:   'Contact Information',
  employee:  'Employees',
  vehicles:  'Vehicles',
  items:     'Market Items',
  registration_timeline: 'Registration & Membership Timeline',
  meetings:  'Meetings',
  owners:    'Owner History',
  documents: 'Documents',
  rent:      'Rent Payments',
  bonds:     'Issue Bonds',
  payment_details: 'Payment Details',
  position:  'Position Type',
};

function Dashboard() {
  const { token } = useAuth();
  const [shops, setShops]       = useState([]);
  const [shopId, setShopId]     = useState('');
  const [shopCode, setShopCode] = useState('');
  const [member, setMember]     = useState(null);
  const [tab, setTab]           = useState('personal');
  const [loadingM, setLoadM]    = useState(false);
  const [view, setView]         = useState('shops'); // 'shops' | 'reports'

  useEffect(() => {
    if (!token) return;
    api.get('/shops').then(r => setShops(r.data)).catch(() => {});
  }, [token]);

  const loadShop = async (id, shops_list) => {
    const list = shops_list || shops;
    setShopId(id);
    const shop = list.find(s => s.id === Number(id));
    setShopCode(shop?.shop_code || '');
    setMember(null);
    if (!id) return;
    setLoadM(true);
    try {
      const { data } = await api.get(`/members/shop/${id}`);
      setMember(data);
    } catch { setMember(null); }
    finally { setLoadM(false); }
  };

  // Called from ShopDetailModal "Edit" button — jump directly to that shop's edit form
  const handleEditShop = (shop) => {
    setTab('personal');
    loadShop(shop.id);
  };

  if (!token) return <Login />;

  const sectionProps = { member, shopId, onSaved: setMember };

  return (
    <Layout
      activeTab={tab}
      onTabChange={setTab}
      shopId={shopId}
      onShopChange={loadShop}
      shops={shops}
    >
      {!shopId ? (
        /* ── Main screen: shops overview or reports ── */
        view === 'shops' ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <ShopsOverview shops={shops} onEditShop={handleEditShop} />
            <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50 }}>
              <button className="btn btn-primary" onClick={() => setView('reports')}>
                📊 View Reports
              </button>
            </div>
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Reports />
            <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50 }}>
              <button className="btn btn-secondary" onClick={() => setView('shops')}>
                ← Back to Shops
              </button>
            </div>
          </div>
        )
      ) : (
        /* ── Shop detail editing view ── */
        <div style={{ padding: '24px 28px' }}>
          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              {/* Back to overview */}
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => { setShopId(''); setShopCode(''); setMember(null); }}
                style={{ marginRight: 4 }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M19 12H5M12 5l-7 7 7 7"/>
                </svg>
                All Shops
              </button>
              <span className="badge badge-blue" style={{ fontSize: 13, padding: '4px 10px' }}>
                {shopCode}
              </span>
              <h2 style={{ fontSize: 20, fontWeight: 600 }}>{TAB_TITLES[tab]}</h2>
            </div>
            {member?.name && (
              <p style={{ color: 'var(--text3)', fontSize: 13, paddingLeft: 2 }}>
                {member.name}{member.member_no ? ` · Member #${member.member_no}` : ''}
              </p>
            )}
          </div>

          {loadingM ? (
            <p style={{ color: 'var(--text3)', padding: '32px 0' }}>Loading…</p>
          ) : (
            <div className="card" style={{ padding: 24 }}>
              {tab === 'personal'  && <Personal  {...sectionProps} />}
              {tab === 'extra_details'     && <ExtraDetails     {...sectionProps} />}
              {tab === 'family'    && <Family    member={member} />}
              {tab === 'qualification' && <Qualification {...sectionProps} />}
              {tab === 'contact'   && <Contact   {...sectionProps} />}
              {tab === 'employee'  && <Employee  member={member} />}
              {tab === 'vehicles'  && <Vehicles  member={member} />}
              {tab === 'items'     && <Items     member={member} />}
              {tab === 'registration_timeline' && <RegistrationTimeline member={member} />}
              {tab === 'meetings'  && <Meetings  member={member} />}
              {tab === 'owners'    && <Owners    member={member} />}
              {tab === 'documents' && <Documents member={member} />}
              {tab === 'rent'      && <Rent      member={member} shops={shops} />}
              {tab === 'bonds'     && <IssuesBonds member={member} shops={shops} />}
              {tab === 'payment_details' && <PaymentDetails member={member} shops={shops} />}
              {tab === 'position'  && <PositionType member={member} />}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <Dashboard />
    </AuthProvider>
  );
}
